/**
 * Public thin intake — Studio V2 Form→Flow→App + CLAW skill if-application-intake.
 * Packet / voice / chat strip + tray receipt/docs are unified here (classic route redirects).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import CitizenAdvisorShell from '../../components/citizen/CitizenAdvisorShell';
import CitizenIntakeChatStrip from '../../components/citizen/CitizenIntakeChatStrip';
import {
  INTAKE_PUBLIC_DEMO_ID,
  loadIntakePublicDemoDefinition,
} from '../../data/interviewFlowIntakeDemo';
import {
  fetchJurisdictionPackage,
  getFallbackJurisdictionPackage,
} from '../../data/sfAdvisorIntake';
import useResolvedJurisdiction from '../../hooks/useResolvedJurisdiction';
import { getDiscoverySessionId } from '../../utils/discoverySession';
import { packageIdFromSearchParams } from '../../utils/citizenAdvisorQueryPrefill';

export default function StudioV2IntakeDemoPage({
  defaultPackageId = 'san-francisco',
} = {}) {
  const [searchParams] = useSearchParams();
  const queryCity = packageIdFromSearchParams(searchParams);
  const resolved = useResolvedJurisdiction({ fallbackPackageId: defaultPackageId });
  const packageId = queryCity
    || (resolved.matched ? resolved.packageId : null)
    || resolved.packageId
    || defaultPackageId;

  const sessionId = searchParams.get('session')
    || searchParams.get('sessionId')
    || getDiscoverySessionId()
    || '';
  const applicationTypeId = searchParams.get('type')
    || searchParams.get('applicationTypeId')
    || 'building_permit';

  const [definition, setDefinition] = useState(null);
  const [pkg, setPkg] = useState(() => getFallbackJurisdictionPackage(packageId));
  const [liveSessionId, setLiveSessionId] = useState(sessionId);

  useEffect(() => {
    setLiveSessionId(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (resolved.loading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const loaded = await fetchJurisdictionPackage(packageId);
        if (!cancelled) setPkg(loaded);
      } catch (_) {
        if (!cancelled) setPkg(getFallbackJurisdictionPackage(packageId));
      }
      const def = await loadIntakePublicDemoDefinition({
        jurisdictionId: packageId,
        sessionId: liveSessionId || sessionId,
        applicationTypeId,
        pkg: pkg || getFallbackJurisdictionPackage(packageId),
      });
      if (!cancelled) setDefinition(def);
    })();
    return () => { cancelled = true; };
  }, [packageId, sessionId, liveSessionId, applicationTypeId, resolved.loading]);

  const cityQs = useMemo(() => {
    const p = new URLSearchParams();
    if (queryCity) p.set('city', queryCity);
    if (liveSessionId || sessionId) p.set('session', liveSessionId || sessionId);
    if (applicationTypeId) p.set('type', applicationTypeId);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [queryCity, sessionId, liveSessionId, applicationTypeId]);

  const portalLinks = [
    {
      to: `/v2/demo/permit${queryCity ? `?city=${encodeURIComponent(queryCity)}` : ''}`,
      label: 'Advisor',
      active: false,
    },
    {
      to: `/v2/demo/permit/intake${cityQs}`,
      label: 'Intake',
      active: true,
    },
    {
      to: `/v2/demo/permit/staff${queryCity ? `?city=${encodeURIComponent(queryCity)}` : ''}`,
      label: 'Staff',
      active: false,
    },
  ];

  const onSession = useCallback((s) => {
    if (s?.sessionId) setLiveSessionId(s.sessionId);
  }, []);

  if (resolved.loading || !definition) {
    return (
      <Box sx={{
        height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' }}>
      <CitizenAdvisorShell
        definition={definition}
        jurisdictionPackage={pkg}
        mongoId={definition.id || INTAKE_PUBLIC_DEMO_ID}
        portalLinks={portalLinks}
        footer={(
          <CitizenIntakeChatStrip
            sessionId={liveSessionId || sessionId}
            packageId={packageId}
            onSession={onSession}
          />
        )}
      />
    </Box>
  );
}
